import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from './auth'

const prefix = 'applications/v1.0'

export const applicationsApi = createApi({
  baseQuery: baseQuery(),
  reducerPath: 'applicationsApi',
  endpoints: (builder) => ({
    listApplications: builder.query<Paginated<ApplicationListItem>, ApplicationsListParams | void>({
      query: (params) => ({
        url: `${prefix}/applications/`,
        params: params || {},
      }),
    }),
    listAdminApplications: builder.query<Paginated<ApplicationAdminListItem>, ApplicationsListParams | void>({
      query: (params) => ({
        url: `${prefix}/adm/applications/`,
        params: params || {},
      }),
    }),
    getApplication: builder.query<ApplicationDetail, string>({
      query: (publicId) => ({ url: `${prefix}/applications/${publicId}/` }),
    }),
    getAdminApplication: builder.query<ApplicationDetail, string>({
      query: (publicId) => ({ url: `${prefix}/adm/applications/${publicId}/` }),
    }),
    createApplication: builder.mutation<ApplicationDetail, ApplicationCreateParams>({
      query: (body) => ({
        url: `${prefix}/applications/`,
        method: 'POST',
        body,
      }),
    }),
    createApplicationFromOrder: builder.mutation<ApplicationDetail, number>({
      query: (orderPk) => ({
        url: `calc-api/v4.0/order/${orderPk}/create-application/`,
        method: 'POST',
      }),
    }),
    confirmCheckItemTnvedChanges: builder.mutation<
      ApplicationDetail,
      ApplicationConfirmTnvedChangesParams
    >({
      query: ({ publicId }) => ({
        url: `${prefix}/applications/${publicId}/confirm-tnved-changes/`,
        method: 'POST',
      }),
    }),
    postCheckItemComment: builder.mutation<
      ApplicationDetail,
      ApplicationPostCheckItemCommentParams
    >({
      transformResponse: (response: ApplicationCheckItemCommentResponse) => response.application,
      query: ({ publicId, kind, body, attachments }) => {
        const files = attachments ?? []
        if (files.length > 0) {
          const form = new FormData()
          form.append('body', body)
          files.forEach((file) => form.append('attachments', file))
          return {
            url: `${prefix}/applications/${publicId}/check-items/${kind}/comments/`,
            method: 'POST',
            body: form,
          }
        }
        return {
          url: `${prefix}/applications/${publicId}/check-items/${kind}/comments/`,
          method: 'POST',
          body: { body },
        }
      },
    }),
    answerClarification: builder.mutation<
      ApplicationDetail,
      ApplicationAnswerClarificationParams
    >({
      transformResponse: (response: ApplicationClarificationAnswerResponse) => response.application,
      query: ({ publicId, clarificationId, answer_text, attachments }) => {
        const files = attachments ?? []
        if (files.length > 0) {
          const body = new FormData()
          body.append('answer_text', answer_text)
          files.forEach((file) => body.append('attachments', file))
          return {
            url: `${prefix}/applications/${publicId}/clarifications/${clarificationId}/answer/`,
            method: 'POST',
            body,
          }
        }
        return {
          url: `${prefix}/applications/${publicId}/clarifications/${clarificationId}/answer/`,
          method: 'POST',
          body: { answer_text },
        }
      },
    }),
    requestExecutorTransfer: builder.mutation<ApplicationDetail, string>({
      query: (publicId) => ({
        url: `${prefix}/applications/${publicId}/request-executor-transfer/`,
        method: 'POST',
      }),
    }),
    requestContactExchange: builder.mutation<ApplicationDetail, ApplicationRequestContactExchangeParams>({
      query: ({ publicId, ...body }) => ({
        url: `${prefix}/applications/${publicId}/request-contact-exchange/`,
        method: 'POST',
        body,
      }),
    }),
    declineProposal: builder.mutation<ApplicationDetail, ApplicationDeclineProposalParams>({
      query: ({ publicId, reason }) => ({
        url: `${prefix}/applications/${publicId}/decline-proposal/`,
        method: 'POST',
        body: reason?.trim() ? { reason: reason.trim() } : {},
      }),
    }),
    downloadApplicationDocument: builder.mutation<ApplicationDocumentDownload, string>({
      query: (url) => ({
        url,
        responseHandler: async (response: Response) => {
          if (!response.ok) return response.text()
          return {
            objectUrl: URL.createObjectURL(await response.blob()),
            disposition: response.headers.get('content-disposition') || '',
          }
        },
      }),
    }),
    listExecutorCompanies: builder.query<
      Paginated<ExecutorCompanyOption>,
      ExecutorCompaniesListParams
    >({
      query: (params) => ({
        url: `${prefix}/adm/executor-companies/`,
        params,
      }),
    }),
    metaStatuses: builder.query<ChoiceItem[], void>({
      query: () => ({ url: `${prefix}/meta/statuses/` }),
    }),
    metaOutcomes: builder.query<ChoiceItem[], void>({
      query: () => ({ url: `${prefix}/meta/outcomes/` }),
    }),
    metaCheckItemStatuses: builder.query<ChoiceItem[], void>({
      query: () => ({ url: `${prefix}/meta/check-item-statuses/` }),
    }),
  }),
})

export const {
  useDownloadApplicationDocumentMutation,
  useListApplicationsQuery,
  useListAdminApplicationsQuery,
  useGetApplicationQuery,
  useGetAdminApplicationQuery,
  useCreateApplicationMutation,
  useCreateApplicationFromOrderMutation,
  useConfirmCheckItemTnvedChangesMutation,
  usePostCheckItemCommentMutation,
  useAnswerClarificationMutation,
  useRequestExecutorTransferMutation,
  useRequestContactExchangeMutation,
  useDeclineProposalMutation,
  useListExecutorCompaniesQuery,
  useMetaStatusesQuery,
  useMetaOutcomesQuery,
  useMetaCheckItemStatusesQuery,
} = applicationsApi
